/**
 * Spells — instant cards of the 'spell' class (shared single source of
 * truth: server validation, frontend card rendering and the tests all read
 * this file).
 *
 * A spell looks like a minion card (name + class badge, a hand slot) but
 * has NO attack and NO health: the card's center carries its effect text.
 *
 * Rules (mirrored by the authoritative server paths):
 *  - SOLD like a card, but the sale value is the spell's own fixed
 *    `sellValue` (NOT level-based — spells are always "level 1" cards);
 *  - GIVEN to a COOP teammate like a minion (the normal give path, same
 *    COOP_GIVE_COST);
 *  - PLAYED by CASTING: the effect goes into play immediately and the card
 *    DESPAWNS (consumed — it never lands on the battlefield, so it has no
 *    level/upgrade/keyword/skin lifecycle).
 *
 * Targeting:
 *  - 'minion' spells are cast by dragging the card onto a friendly
 *    battlefield minion (the target — the card is consumed and the effect
 *    applies to that minion);
 *  - 'battlefield' spells are cast by releasing the card anywhere over the
 *    battlefield (no target).
 *
 * ACQUISITION: the spells are NOT part of the base `testMinions` draw pool —
 * they are drawn ONLY through the Rune of the Mage (RuneDefinitions): while
 * the owner (or, in COOP, the owner's teammate) has the rune, every card
 * drawn has a chance — equal to any single class card — to be a RANDOM
 * level-1 spell (see getSpellDefinitionsByLevel). The definitions below
 * remain the single content source.
 */

import { MinionDefinition, MinionDefinitionValidator } from './MinionDefinition';
import { MinionType, VALID_MINION_TYPES } from './MinionType';

/** The spell class (a MinionType — spells are class cards like items). */
export const SPELL_MINION_TYPE = 'spell';

/** What a spell targets when it is cast. */
export type SpellTarget = 'minion' | 'battlefield';

/** The content spec of one spell card. */
export interface SpellDefinition {
  /** The base minion ID of the spell card (the MinionDefinition id). */
  id: string;
  /** Display name (top of the card). */
  name: string;
  /** The spell's class (always 'spell'). */
  minionType: 'spell';
  /** What the spell targets when cast. */
  target: SpellTarget;
  /** Gold awarded when the spell is SOLD (fixed — spells have no level). */
  sellValue: number;
  /** The spell's LEVEL, rendered centered at the bottom of the card.
   *  Spells are instant cards (cast + despawn — no battlefield upgrades),
   *  so the level is a fixed attribute of the spec, not upgradeCount + 1.
   *  The Rune of the Mage draws a random LEVEL-1 spell. */
  level: number;
  /** The effect text, rendered centered on the card. */
  description: string;
}

// ─── Spell IDs ─────────────────────────────────────────────────────────────

/** Power touch — target a friendly minion, permanently +5/+5. */
export const POWER_TOUCH_SPELL_ID = 'power_touch';

/** Healing Rain — for the next combat, every friendly attack heals the
 *  whole friendly row by 1 HP. */
export const HEALING_RAIN_SPELL_ID = 'healing_rain';

// ─── New spell IDs (Task: New Spells) ──────────────────────────────────────

/** Acid Rain — for the next 4 combats, every attack deals 1 damage to ALL
 *  minions (green raindrops fall on the battlefield while active). */
export const ACID_RAIN_SPELL_ID = 'acid_rain';

/** Treasure Hunter — target a friendly minion: its first kill in combat
 *  spawns a golden treasure (item) on the ENEMY side at the killed minion's
 *  position. */
export const TREASURE_HUNTER_SPELL_ID = 'treasure_hunter';

/** Transformed — target a friendly minion: it becomes a RANDOM other class
 *  (never the same class, never a Swarmer), keeping its level and current
 *  stats but losing ALL keywords. */
export const TRANSFORMED_SPELL_ID = 'transformed';

/** Delevel — target a friendly minion: remove 1 level WITHOUT removing
 *  stats or keywords. */
export const DELEVEL_SPELL_ID = 'delevel';

/** Divine Blessing — target a friendly minion: the first damage it takes
 *  in combat is ignored (large yellow explosion on the minion when it
 *  triggers). */
export const DIVINE_BLESSING_SPELL_ID = 'divine_blessing';

/** Infested — target a friendly minion: it has the SwarmLord keyword in
 *  the next combat, and is removed afterwards. */
export const INFESTED_SPELL_ID = 'infested';

/** Improved — the Improved ability gets +2/+2 (as if two Undead-class
 *  units had been played — the match-wide Improved counter advances by 2). */
export const IMPROVED_SPELL_ID = 'improved';

/** Upgraded — target a friendly minion: +8 HP / +8 attack permanently. */
export const UPGRADED_SPELL_ID = 'upgraded';

/** Blizzard — for the next combat only, before every attack there is a 25%
 *  chance the attacking minion is Frozen INSTEAD of attacking (snowflakes
 *  whirl around the screen during that combat). */
export const BLIZZARD_SPELL_ID = 'blizzard';

/** Protector (task 183) — target a friendly minion: it PERMANENTLY gains
 *  the Shield keyword (taunt — enemies must attack it while it lives). */
export const PROTECTOR_SPELL_ID = 'protector';

/** Sacrifice (task 186) — target a friendly minion: it is DESTROYED (no
 *  compensation) and the caster DRAWS as many cards as the minion's level
 *  (level 1 → 1 card, level 6 → 6 cards). */
export const SACRIFICE_SPELL_ID = 'sacrifice';

/** Ban Hammer (task 193) — target a friendly minion: the target's CLASS
 *  is EXCLUDED FROM THE CASTER'S draw pool for the rest of the game (the
 *  caster can never draw that class again; every OTHER player is
 *  unaffected). The ban is a per-caster draw-pool filter in
 *  CardDrawService — the target minion itself is untouched. */
export const BAN_HAMMER_SPELL_ID = 'ban_hammer';

/** Infestation (task 194) — battlefield: for the NEXT COMBAT ONLY,
 *  whenever ANY minion dies (on EITHER side) a Fragile Swarmer (a SWARMER
 *  token — task 204) replaces the dead minion in place and attacks
 *  immediately (a full new exchange). The effect resolves AFTER everything
 *  else (the RoundCoordinator's top-of-loop scan — the previous
 *  exchange's full death pipeline is done), only applies if there is SPACE
 *  on the dead minion's side, and does NOT trigger when a token dies (no
 *  chains). All minions in the combat are covered — not only friendly
 *  ones. */
export const INFESTATION_SPELL_ID = 'infestation';

/** Enrichment (task 197) — battlefield: gives the caster GOLD when played
 *  — 1 gold plus 1 more for every TURN the card sat in the hand (a
 *  per-card counter, MinionInstance.enrichmentTurns: drawn this turn →
 *  1 gold, one turn later → 2, … the counter increments at every new
 *  preparation turn for every Enrichment card in the hand; several
 *  Enrichment cards each carry their OWN counter). The card is consumed
 *  on the cast (the golden rain plays over the whole playfield). */
export const ENRICHMENT_SPELL_ID = 'enrichment';

/** Draw (task 219) — battlefield: DRAW 2 CARDS immediately when played
 *  (each draw through the shared overdraw hook — a full hand loses the
 *  card: the red-card explosion event + the Sacrificial Rune trigger,
 *  exactly like the turn draws / Sacrifice's draws). */
export const DRAW_SPELL_ID = 'draw';

// ─── Spell values ──────────────────────────────────────────────────────────

/** The fixed sell value of a spell card (both current spells). */
export const SPELL_SELL_VALUE = 5;

/** Power touch: the PERMANENT +N/+N granted to the target minion. */
export const POWER_TOUCH_STAT_BONUS = 5;

/** Enrichment: the BASE gold the cast pays (1 — plus 1 more per turn the
 *  card sat in the hand, MinionInstance.enrichmentTurns). */
export const ENRICHMENT_BASE_GOLD = 1;

/** Draw (task 219): the number of cards the cast draws IMMEDIATELY. */
export const DRAW_SPELL_CARD_COUNT = 2;

/** Healing Rain: HP restored to EVERY living friendly minion per friendly
 *  attack (capped at each copy's max HP, no resurrection). Rebuilt to 2 in
 *  task 183 ("Healing rain should heal 2 hp"). */
export const HEALING_RAIN_HEAL_AMOUNT = 2;

// ─── Catalogue ─────────────────────────────────────────────────────────────

/** The definitive spell catalogue (APPEND new spells here). */
export const SPELL_DEFINITIONS: SpellDefinition[] = [
  {
    id: POWER_TOUCH_SPELL_ID,
    name: 'Power touch',
    minionType: SPELL_MINION_TYPE,
    target: 'minion',
    sellValue: SPELL_SELL_VALUE,
    level: 1,
    description:
      'Give the target minion +5 HP and +5 attack.',
  },
  {
    id: HEALING_RAIN_SPELL_ID,
    name: 'Healing Rain',
    minionType: SPELL_MINION_TYPE,
    target: 'battlefield',
    sellValue: SPELL_SELL_VALUE,
    level: 1,
    description:
      'Next combat: when any of YOUR minions attacks, your minions restore 2 HP.',
  },
  // ─── New Spells (Task: New Spells) ─────────────────────────────────────
  {
    id: ACID_RAIN_SPELL_ID,
    name: 'Acid Rain',
    minionType: SPELL_MINION_TYPE,
    target: 'battlefield',
    sellValue: 7,
    level: 2,
    description:
      'On the next 4 attacks deal 1 damage to ALL minions.'
  },
  {
    id: TREASURE_HUNTER_SPELL_ID,
    name: 'Treasure Hunter',
    minionType: SPELL_MINION_TYPE,
    target: 'minion',
    sellValue: 7,
    level: 2,
    description:
      'Next combat: first enemy killed by this is replaced in combat by a golden treasure',
  },
  {
    id: TRANSFORMED_SPELL_ID,
    name: 'Transformed',
    minionType: SPELL_MINION_TYPE,
    target: 'minion',
    sellValue: 1,
    level: 1,
    description:
      'Transform the minion into a random other class. It is reset to level 1 (the new class base hp/atk) and loses all keywords.',
  },
  {
    id: DELEVEL_SPELL_ID,
    name: 'Delevel',
    minionType: SPELL_MINION_TYPE,
    target: 'minion',
    sellValue: 6,
    level: 2,
    description:
      'Remove 1 level from the minion, without removing its stats or keywords.',
  },
  {
    id: DIVINE_BLESSING_SPELL_ID,
    name: 'Divine Blessing',
    minionType: SPELL_MINION_TYPE,
    target: 'minion',
    sellValue: 4,
    level: 1,
    description:
      'Next combat: the first time this minion takes damage, ignore it'
  },
  {
    id: INFESTED_SPELL_ID,
    name: 'Infested',
    minionType: SPELL_MINION_TYPE,
    target: 'minion',
    sellValue: 8,
    level: 2,
    description:
      'Next combat: This minion gains the SwarmLord keyword.',
  },
  {
    id: IMPROVED_SPELL_ID,
    name: 'Improved',
    minionType: SPELL_MINION_TYPE,
    target: 'battlefield',
    sellValue: 1,
    level: 1,
    description:
      'The Improved ability gets +2/+2 ',
  },
  {
    id: UPGRADED_SPELL_ID,
    name: 'Upgraded',
    minionType: SPELL_MINION_TYPE,
    target: 'minion',
    sellValue: 3,
    level: 2,
    description:
      'Give the target minion +8 HP and +8 attack permanently - improves by +4/+4 for every previous cast',
  },
  {
    id: BLIZZARD_SPELL_ID,
    name: 'Blizzard',
    minionType: SPELL_MINION_TYPE,
    target: 'battlefield',
    sellValue: 6,
    level: 2,
    description:
      'Next combat: before any minion attacks, there is a 25% chance it is Frozen and skips that attack.',
  },
  {
    id: PROTECTOR_SPELL_ID,
    name: 'Protector',
    minionType: SPELL_MINION_TYPE,
    target: 'minion',
    sellValue: 7,
    level: 2,
    description:
      'Permanently give the target minion Shield',
  },
  {
    id: SACRIFICE_SPELL_ID,
    name: 'Sacrifice',
    minionType: SPELL_MINION_TYPE,
    target: 'minion',
    sellValue: 5,
    level: 2,
    description:
      'Destroy the target minion. Draw as many cards as its level',
  },
  {
    id: BAN_HAMMER_SPELL_ID,
    name: 'Ban Hammer',
    minionType: SPELL_MINION_TYPE,
    target: 'minion',
    sellValue: 10,
    level: 3,
    description:
      'The target minion’s class is EXCLUDED from YOUR draw pile',
  },
  {
    id: INFESTATION_SPELL_ID,
    name: 'Infestation',
    minionType: SPELL_MINION_TYPE,
    target: 'battlefield',
    sellValue: 6,
    level: 3,
    description:
      'Next combat: whenever ANY minion dies, SWARMER replaces the dead minion and attacks immediately',
  },
  {
    id: DRAW_SPELL_ID,
    name: 'Draw',
    minionType: SPELL_MINION_TYPE,
    target: 'battlefield',
    sellValue: 2,
    level: 1,
    description: 'Draw 2 cards.',
  },
  {
    id: ENRICHMENT_SPELL_ID,
    name: 'Enrichment',
    minionType: SPELL_MINION_TYPE,
    target: 'battlefield',
    sellValue: 1,
    level: 1,
    description:
      'Gives 1 gold when played improve by 1 gold every turn it stays in the hand.',
  },
];

/**
 * Looks up the spell spec by base minion ID (null for non-spells).
 */
export function getSpellDefinition(baseMinionId: string): SpellDefinition | null {
  return SPELL_DEFINITIONS.find((d) => d.id === baseMinionId) ?? null;
}

// ─── Card definitions (the draw-pool-style MinionDefinitions) ──────────────
//
// Exported (the locked-class / item pattern) and deliberately NOT part of
// the `testMinions` base draw pool: 0/0 stats, no abilities, no upgrade
// paths — the spell's identity lives in SpellDefinition, not in stats.

/** The Power touch card definition (see POWER_TOUCH_SPELL_ID). */
export const POWER_TOUCH_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: POWER_TOUCH_SPELL_ID,
  name: 'Power touch',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Healing Rain card definition (see HEALING_RAIN_SPELL_ID). */
export const HEALING_RAIN_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: HEALING_RAIN_SPELL_ID,
  name: 'Healing Rain',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

// ─── New spell card definitions (Task: New Spells) ──────────────────────────
// The same 0/0 spell-card shape as the original two (see
// POWER_TOUCH_MINION_DEFINITION).

/** The Acid Rain card definition (see ACID_RAIN_SPELL_ID). */
export const ACID_RAIN_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: ACID_RAIN_SPELL_ID,
  name: 'Acid Rain',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Treasure Hunter card definition (see TREASURE_HUNTER_SPELL_ID). */
export const TREASURE_HUNTER_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: TREASURE_HUNTER_SPELL_ID,
  name: 'Treasure Hunter',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Transformed card definition (see TRANSFORMED_SPELL_ID). */
export const TRANSFORMED_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: TRANSFORMED_SPELL_ID,
  name: 'Transformed',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Delevel card definition (see DELEVEL_SPELL_ID). */
export const DELEVEL_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: DELEVEL_SPELL_ID,
  name: 'Delevel',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Divine Blessing card definition (see DIVINE_BLESSING_SPELL_ID). */
export const DIVINE_BLESSING_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: DIVINE_BLESSING_SPELL_ID,
  name: 'Divine Blessing',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Infested card definition (see INFESTED_SPELL_ID). */
export const INFESTED_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: INFESTED_SPELL_ID,
  name: 'Infested',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Improved card definition (see IMPROVED_SPELL_ID). */
export const IMPROVED_SPELL_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: IMPROVED_SPELL_ID,
  name: 'Improved',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Upgraded card definition (see UPGRADED_SPELL_ID). */
export const UPGRADED_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: UPGRADED_SPELL_ID,
  name: 'Upgraded',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Blizzard card definition (see BLIZZARD_SPELL_ID). */
export const BLIZZARD_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: BLIZZARD_SPELL_ID,
  name: 'Blizzard',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Protector card definition (see PROTECTOR_SPELL_ID, task 183). */
export const PROTECTOR_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: PROTECTOR_SPELL_ID,
  name: 'Protector',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Sacrifice card definition (see SACRIFICE_SPELL_ID, task 186). */
export const SACRIFICE_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: SACRIFICE_SPELL_ID,
  name: 'Sacrifice',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Ban Hammer card definition (see BAN_HAMMER_SPELL_ID, task 193). */
export const BAN_HAMMER_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: BAN_HAMMER_SPELL_ID,
  name: 'Ban Hammer',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Infestation card definition (see INFESTATION_SPELL_ID, task 194). */
export const INFESTATION_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: INFESTATION_SPELL_ID,
  name: 'Infestation',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Enrichment card definition (see ENRICHMENT_SPELL_ID, task 197). */
export const ENRICHMENT_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: ENRICHMENT_SPELL_ID,
  name: 'Enrichment',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/** The Draw card definition (see DRAW_SPELL_ID, task 219). */
export const DRAW_MINION_DEFINITION: MinionDefinition = MinionDefinitionValidator.validate({
  id: DRAW_SPELL_ID,
  name: 'Draw',
  startingHp: 0,
  attack: 0,
  minionType: SPELL_MINION_TYPE,
  baseAbilities: [],
  allowedUpgradePaths: [],
});

/**
 * Spell id → card-definition map (the draw-pool form of each spell).
 * Kept in sync with SPELL_DEFINITIONS — the single content source.
 */
const SPELL_MINION_DEFINITION_BY_ID: Map<string, MinionDefinition> = new Map([
  [POWER_TOUCH_SPELL_ID, POWER_TOUCH_MINION_DEFINITION],
  [HEALING_RAIN_SPELL_ID, HEALING_RAIN_MINION_DEFINITION],
  [ACID_RAIN_SPELL_ID, ACID_RAIN_MINION_DEFINITION],
  [TREASURE_HUNTER_SPELL_ID, TREASURE_HUNTER_MINION_DEFINITION],
  [TRANSFORMED_SPELL_ID, TRANSFORMED_MINION_DEFINITION],
  [DELEVEL_SPELL_ID, DELEVEL_MINION_DEFINITION],
  [DIVINE_BLESSING_SPELL_ID, DIVINE_BLESSING_MINION_DEFINITION],
  [INFESTED_SPELL_ID, INFESTED_MINION_DEFINITION],
  [IMPROVED_SPELL_ID, IMPROVED_SPELL_MINION_DEFINITION],
  [UPGRADED_SPELL_ID, UPGRADED_MINION_DEFINITION],
  [BLIZZARD_SPELL_ID, BLIZZARD_MINION_DEFINITION],
  [PROTECTOR_SPELL_ID, PROTECTOR_MINION_DEFINITION],
  [SACRIFICE_SPELL_ID, SACRIFICE_MINION_DEFINITION],
  [BAN_HAMMER_SPELL_ID, BAN_HAMMER_MINION_DEFINITION],
  [INFESTATION_SPELL_ID, INFESTATION_MINION_DEFINITION],
  [ENRICHMENT_SPELL_ID, ENRICHMENT_MINION_DEFINITION],
  [DRAW_SPELL_ID, DRAW_MINION_DEFINITION],
]);

/**
 * Looks up the card definition (MinionDefinition) for a spell id, or null.
 */
export function getSpellMinionDefinition(id: string): MinionDefinition | null {
  return SPELL_MINION_DEFINITION_BY_ID.get(id) ?? null;
}

/**
 * The card definitions of EVERY level-`level` spell in the catalogue.
 * The Rune of the Mage draws a RANDOM entry from this (level 1) list — so a
 * future level-2 spell is automatically excluded from the draw pool, and a
 * future level-1 spell is automatically included.
 */
export function getSpellDefinitionsByLevel(level: number): MinionDefinition[] {
  return SPELL_DEFINITIONS
    .filter((s) => s.level === level)
    .map((s) => getSpellMinionDefinition(s.id))
    .filter((d): d is MinionDefinition => d !== null);
}

/**
 * The candidate target classes of the TRANSFORMED spell ("a random class
 * minion — not the same, not a Swarmer"): every MINION class that can
 * stand on a battlefield — `swarmer` (the user's explicit exclusion),
 * `spell` and `item` (not minion classes — spells never land on the board
 * and items are not a "random class" target) are excluded; the caster's
 * own class is excluded by the caller (the minion "not the same"). (Task 204
 * removed the accidental `spawner` class — its filter term is gone with
 * the class.)
 */
export const TRANSFORMED_SPELL_TARGET_CLASSES: readonly MinionType[] = VALID_MINION_TYPES.filter(
  (t) => t !== 'swarmer' && t !== 'spell' && t !== 'item',
);

/**
 * The card definitions of every spell with level ≤ `maxLevel` (catalogue
 * order).
 *
 * The SPELL DRAW POOL for spell power N (the Spellmaker / SpellMaster / Rune
 * of the Mage rule — see AbilityConstants): power N draws a RANDOM entry
 * from this list with N = maxLevel, so "up to level N spells" are
 * drawable — power 1: level-1 spells, power 2: levels 1-2, power 3:
 * levels 1-3, and so on.
 */
export function getSpellDefinitionsUpToLevel(maxLevel: number): MinionDefinition[] {
  return SPELL_DEFINITIONS
    .filter((s) => s.level <= maxLevel)
    .map((s) => getSpellMinionDefinition(s.id))
    .filter((d): d is MinionDefinition => d !== null);
}
